import { DocumentNode } from '@apollo/client';
import { EntityName } from '@provision/entity-types';
import { hashStringToHex, stableStringify } from '@provision/formatting-utils';
import { QueryVariables } from 'src/graphql/types';

export interface RegisteredQuery {
  key: string;
  entityName: EntityName;
  query: DocumentNode;
  operationName: string;
  timestamp: number;
  variables: QueryVariables & QueryVariables['params'];
}

// This class manages a collection of queries. class construct is used to be able to
// export singleton instance of QueryRegistry for use throughout the application
class QueryRegistry {
  private queries = new Map<string, RegisteredQuery>();
  private byEntity = new Map<EntityName, Set<string>>();

  private makeKey(operationName: string, variables: RegisteredQuery['variables']) {
    const serialized = stableStringify(variables);
    // simple—no extra code paths to store short unique string in the map instead
    // of storing big objects that could consume too many resources
    const varsHash = hashStringToHex(serialized);

    return `${operationName}:${varsHash}`;
  }

  register(params: {
    entityName: EntityName;
    query: DocumentNode;
    operationName: string;
    variables: RegisteredQuery['variables'];
  }) {
    const { entityName, operationName, variables, query } = params;
    const key = this.makeKey(operationName, variables);
    const now = Date.now();

    const rq: RegisteredQuery = {
      key,
      entityName,
      query,
      operationName,
      variables,
      timestamp: now,
    };

    // store in main map
    this.queries.set(key, rq);

    // add to per-entity set
    let set = this.byEntity.get(entityName);
    if (!set) {
      set = new Set<string>();
      this.byEntity.set(entityName, set);
    }
    set.add(key);
  }

  remove(key: string) {
    const rq = this.queries.get(key);
    if (!rq) return;

    this.queries.delete(key);

    const set = this.byEntity.get(rq.entityName);

    if (set) {
      set.delete(rq.key);
      if (set.size === 0) {
        this.byEntity.delete(rq.entityName);
      }
    }
  }

  // get the live Set of queries for an entity
  getQueriesForEntity(entity: EntityName): RegisteredQuery[] {
    const keys = this.byEntity.get(entity) ?? new Set();
    return Array.from(keys, (k) => this.queries.get(k)!);
  }

  getAllQueries(): RegisteredQuery[] {
    return Array.from(this.queries.values());
  }

  clear() {
    this.queries.clear();
    this.byEntity.clear();
  }
}

export const queryRegistry = new QueryRegistry();
